import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authAPI, setUnauthorizedHandler } from '../api/client';
import { isTransportError } from '../utils/errors';

const AuthContext = createContext(null);

// Why the session ended, so the login screen can say so instead of appearing
// for no visible reason. Null means "no session has ended in this tab".
const SIGNED_OUT = {
  loading: false,
  authenticated: false,
  user: null,
  realUser: null,
  impersonating: false,
  permissions: {},
  isCommitteeMember: false,
  mustChangePassword: false,
  homeScope: null,
  delegation: null,
  connectionError: false,
};

/**
 * Holds "who am I" for the whole app.
 *
 * Everything here is derived from GET /api/auth/me rather than kept as local
 * state, so a refresh cannot desync the UI from the server -- which matters most
 * for the impersonation banner, where a stale local flag means acting as someone
 * else with no visible indication.
 */
export function AuthProvider({ children }) {
  const [state, setState] = useState({
    loading: true,          // true until the first /me resolves
    authenticated: false,
    user: null,             // effective user (the impersonated one, if any)
    realUser: null,         // the human who logged in
    impersonating: false,
    permissions: {},
    mustChangePassword: false,
    homeScope: null,        // {nodes: [...]}, this user's default locations (issue #17)
    // Whose authority is currently on loan (issue #44), or null. NOT a
    // permission grant: `user` and `permissions` above stay this person's own
    // throughout a delegation, so this only tells the UI what to SAY.
    delegation: null,
    connectionError: false,  // last /me could not reach the API
    signOutReason: null,     // 'user' | 'inactivity' | 'expired'
  });

  const applyMe = useCallback((data) => {
    setState((prev) => ({
      ...prev,
      loading: false,
      authenticated: !!data.authenticated,
      user: data.user || null,
      realUser: data.real_user || null,
      impersonating: !!data.impersonating,
      permissions: data.permissions || {},
      // Sidebar affordance (issue #138, narrowed by #150), NOT a permission:
      // "does this caller have a real, actionable approvals queue" --
      // authority over a gate is ApprovalGroupMember (or a live delegation
      // covering one), never an `approvals` RBAC grant. This used to be OR'd
      // with `has_roles` ("holds any role assignment at all"), which made
      // every Read Only user see a link to a queue that is always empty --
      // removed rather than kept dark. Authorizes nothing; only decides
      // whether the link is drawn, and the screen behind it re-authorizes
      // server-side regardless.
      isCommitteeMember: !!data.is_committee_member,
      mustChangePassword: !!data.must_change_password,
      // The EFFECTIVE user's home scope -- while impersonating this is the
      // impersonated user's, matching `user`/`permissions` above (see
      // routers/auth.py::read_current_user's own comment on this).
      homeScope: data.home_scope || null,
      delegation: data.delegation || null,
      connectionError: false,
      signOutReason: data.authenticated ? null : prev.signOutReason,
    }));
  }, []);

  /**
   * Re-read /me. Returns { status: 'ok' | 'unauthenticated' | 'unreachable' }.
   *
   * The distinction is the point. /me answers 200 either way, so ANY error here
   * is a transport failure -- and this used to drop straight to
   * authenticated:false, which meant a cold or briefly-unreachable API threw
   * the user out of a perfectly valid session with no explanation (issue #24).
   *
   * "Fail closed in the UI" still holds where it means something: the FIRST /me
   * of a page load can't be resolved from nothing, so it stays failed-closed.
   * But once we know who someone is, a missed round trip is not evidence they
   * signed out. Nothing is weakened by keeping the shell painted: /me is not an
   * authorization decision, every data request is independently re-authorized
   * server-side against the session row, and the real backstops
   * (session_service._is_live's idle/absolute expiry, revoke_session, and the
   * 401 interceptor below) are untouched. The residual is cosmetic -- a session
   * revoked while the client is offline leaves stale chrome until the next
   * request 401s it away.
   */
  const refresh = useCallback(async () => {
    try {
      const response = await authAPI.me();
      applyMe(response.data);
      return { status: 'ok', data: response.data };
    } catch (error) {
      console.error('Could not resolve identity:', error);
      if (isTransportError(error)) {
        setState((prev) => ({
          ...prev,
          loading: false,
          connectionError: true,
          // Keep identity if we already had one; a page load that has never
          // resolved anyone stays signed out.
          authenticated: prev.loading ? false : prev.authenticated,
          user: prev.loading ? null : prev.user,
        }));
        return { status: 'unreachable', data: null };
      }
      // A response arrived and it wasn't a 200 -- the server has spoken.
      setState((prev) => ({ ...prev, ...SIGNED_OUT, signOutReason: prev.signOutReason }));
      return { status: 'unauthenticated', data: null };
    }
  }, [applyMe]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // When any request 401s, the session is gone -- drop to the login screen.
  // Registered here rather than in client.js because this is where the state
  // that has to change actually lives.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      // A 401 on a normal endpoint IS the server saying the session is gone --
      // most often server-side idle expiry, which the browser's own timer never
      // saw (a closed tab, a slept laptop). Name it, so /login can explain it.
      setState({ ...SIGNED_OUT, signOutReason: 'expired' });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (email, password) => {
    await authAPI.login(email, password);
    // Re-read /me rather than trusting the login response, so there is exactly
    // one shape of identity in this app and one place it comes from.
    setState((prev) => ({ ...prev, signOutReason: null, connectionError: false }));
    return refresh();
  }, [refresh]);

  /**
   * `reason` is recorded for the login screen: 'user' (they clicked sign out)
   * says nothing, 'inactivity' (IdleTimeout gave up) explains itself.
   */
  const logout = useCallback(async (reason = 'user') => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Never let a failed/timed-out logout reject out of here: IdleTimeout
      // awaits this inside an interval callback, where an unhandled rejection
      // wedges the component. The session dies server-side on idle anyway.
      console.error('Logout call failed; clearing local state anyway:', error);
    } finally {
      // Clear local state even if the call failed. The alternative is a UI that
      // claims you are still logged in after you asked not to be.
      setState({ ...SIGNED_OUT, signOutReason: reason });
    }
  }, []);

  const impersonate = useCallback(async (userId) => {
    await authAPI.impersonate(userId);
    // Refresh rather than trusting the response: the banner and every
    // permission-gated affordance must come from the same /me the rest of the
    // app uses, or they can disagree.
    return refresh();
  }, [refresh]);

  const stopImpersonating = useCallback(async () => {
    await authAPI.stopImpersonating();
    return refresh();
  }, [refresh]);

  // Entering/leaving "acting for X" mode (issue #44). Same refresh-don't-trust
  // rule as impersonate above, and for a sharper reason: the delegation banner
  // is the ONLY thing on screen saying whose authority is in use, so it must
  // come from /me rather than a local flag a refresh would drop.
  const startDelegation = useCallback(async (delegationId) => {
    await authAPI.startDelegation(delegationId);
    return refresh();
  }, [refresh]);

  const stopDelegation = useCallback(async () => {
    await authAPI.stopDelegation();
    return refresh();
  }, [refresh]);

  /**
   * can('documents', 'read') -- an AFFORDANCE HINT ONLY.
   *
   * The backend computes this map with any_scope=True, so a true here means
   * "somewhere in the hierarchy", NOT "for the document you are looking at".
   * Use it to hide buttons that would certainly fail. Never use it to decide
   * whether an action is allowed -- that decision belongs to the server, which
   * re-checks with the actual target node.
   */
  const can = useCallback((resource, action) => {
    const allowed = state.permissions[resource];
    return Array.isArray(allowed) && allowed.includes(action);
  }, [state.permissions]);

  const value = useMemo(
    () => ({
      ...state, login, logout, refresh, can, impersonate, stopImpersonating,
      startDelegation, stopDelegation,
    }),
    [state, login, logout, refresh, can, impersonate, stopImpersonating,
     startDelegation, stopDelegation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return context;
}
